/**
 * Designer-authored page backgrounds for the built-in palettes, copied VERBATIM from the Figma
 * "Dashboard Themes v1" gradient frames (one Light/Dark Gradient frame per palette; canonical
 * light frame for Ocean is 2884:48806). Do not hand-tune numbers here — re-extract from Figma.
 *
 * Geometry model (see buildThemeFromSpec.buildPageGradient for the renderer):
 *  - `header`: overlay layers of the 700px-tall top band, top-most layer first. Radial layers are
 *    Figma vector gradients kept as raw gradientTransform matrices in a 1781x700 viewBox; linear
 *    layers are plain CSS gradients.
 *  - `headerBase`: the band's 180deg base wash; its last stop doubles as the page's flat base
 *    color, so the band blends seamlessly into the body.
 *  - `page`: the "Page Gradient" rect drawn below the band (Figma group opacity + layers,
 *    180-degree element rotations already baked into the matrices/angles).
 *  - `noBg`: fixed page colors when the background toggle is OFF — a solid for light mode and a
 *    180deg two-stop gradient for dark mode (the palette-tinted near-black frames in Figma).
 */

/** [color, position] — position is a 0-1 offset for radial stops, a percentage for linear stops. */
export type GradientStop = [string, number];

export interface RadialLayer {
  kind: "radial";
  /** Fill opacity of the layer rect. */
  opacity: number;
  /** SVG gradientTransform matrix [a, b, c, d, tx, ty] in the frame's viewBox space. */
  matrix: [number, number, number, number, number, number];
  stops: GradientStop[];
}

export interface LinearLayer {
  kind: "linear";
  angle: number;
  stops: GradientStop[];
}

export type GradientLayer = RadialLayer | LinearLayer;

export interface ModeBackground {
  header: GradientLayer[];
  headerBase: LinearLayer;
  page: {
    opacity: number;
    /** Frame size the page vectors were authored in (matrix coordinate space). */
    viewBox: [number, number];
    layers: GradientLayer[];
  };
}

export interface PaletteBackground {
  light: ModeBackground;
  dark: ModeBackground;
  noBg: {
    light: string;
    /** [from, to] of a 180deg gradient. */
    dark: [string, string];
  };
}

export const PALETTE_BACKGROUNDS: Record<string, PaletteBackground> = {
  "palette:ocean": {
    light: {
      header: [
        {
          kind: "linear",
          angle: 12.09477,
          stops: [
            ["rgba(255,255,255,0)", 38.318],
            ["rgba(255,255,255,0.5)", 54.504],
            ["rgba(255,255,255,0)", 70.505],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [-28.8, 0, 0, -27.475, 1550, 350],
          stops: [
            ["#3053FF", 0],
            ["rgba(48,83,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.9,
          matrix: [48.6, 0, 0, 33.572, 58, 61.25],
          stops: [
            ["#FFFFFF", 0],
            ["rgba(255,255,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.35,
          matrix: [32.111, 7.525, -2.8255, 36.925, 466, 330.75],
          stops: [
            ["#3053FF", 0],
            ["rgba(48,83,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["#08C8A4", 0.46635],
            ["rgba(8,200,164,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [84.1, 1.75, -0.52073, 25.025, 940, 428.75],
          stops: [
            ["#08C8A4", 0],
            ["rgba(8,200,164,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#B1E9FF", 3.0848],
          ["#EBF2FF", 80.72],
        ],
      },
      page: {
        opacity: 0.1,
        viewBox: [1781, 1296],
        layers: [
          {
            kind: "linear",
            angle: 336.77281,
            stops: [
              ["rgba(0,183,255,0)", 23.111],
              ["#00B7FF", 36.318],
              ["#08C8A4", 42.909],
              ["rgba(8,200,164,0)", 53.897],
            ],
          },
          {
            kind: "radial",
            opacity: 1,
            matrix: [-89.05, -64.8, 89.05, -64.8, 890.5, 648],
            stops: [
              ["#3053FF", 0],
              ["rgba(70,42,231,0.5)", 0.3],
              ["rgba(92,0,206,0)", 0.6],
            ],
          },
        ],
      },
    },
    dark: {
      header: [
        {
          kind: "linear",
          angle: 16.26021,
          stops: [
            ["rgba(107,255,227,0)", 39.416],
            ["rgba(107,255,227,0.2)", 50.7],
            ["rgba(107,255,227,0)", 62.425],
          ],
        },
        {
          kind: "radial",
          opacity: 0.1,
          matrix: [-28.8, 0, 0, -27.475, 1550, 350],
          stops: [
            ["#6BFFE3", 0],
            ["rgba(107,255,227,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.9,
          matrix: [48.6, 0, 0, 33.572, 58, 61.25],
          stops: [
            ["#0A0B0F", 0],
            ["rgba(10,11,15,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.4,
          matrix: [32.111, 7.525, -2.8255, 36.925, 466, 330.75],
          stops: [
            ["#3053FF", 0],
            ["rgba(48,83,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["#08C8A4", 0.46635],
            ["rgba(8,200,164,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [84.1, 1.75, -0.20833, 30.661, 940, 428.75],
          stops: [
            ["#08C8A4", 0],
            ["rgba(8,200,164,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#11102A", 9.4053],
          ["rgba(0,183,255,0.6)", 40.026],
          ["#11102A", 80.72],
        ],
      },
      page: {
        opacity: 0.2,
        viewBox: [1781, 1293],
        layers: [
          {
            kind: "linear",
            angle: 336.82089,
            stops: [
              ["rgba(0,183,255,0)", 23.111],
              ["#00B7FF", 36.318],
              ["#08C8A4", 42.909],
              ["rgba(8,200,164,0)", 53.897],
            ],
          },
          {
            kind: "radial",
            opacity: 1,
            matrix: [-89.05, -64.65, 89.05, -64.65, 890.5, 646.5],
            stops: [
              ["#3053FF", 0],
              ["rgba(48,83,255,0)", 0.6],
            ],
          },
        ],
      },
    },
    noBg: { light: "#EBF2FF", dark: ["#0A0B0F", "#11102A"] },
  },
  "palette:berry": {
    light: {
      header: [
        {
          kind: "radial",
          opacity: 0.2,
          matrix: [-28.8, 0, 0, -27.475, 1550, 350],
          stops: [
            ["#FFFFFF", 0],
            ["rgba(255,255,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.9,
          matrix: [48.6, 0, 0, 33.572, 58, 61.25],
          stops: [
            ["#FFFFFF", 0],
            ["rgba(255,255,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.2,
          matrix: [32.111, 7.525, -2.8255, 36.925, 466, 330.75],
          stops: [
            ["#E31171", 0],
            ["rgba(227,17,113,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["rgba(255,183,0,0.84615)", 0.46635],
            ["rgba(255,183,0,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [84.1, 1.75, -0.20833, 30.661, 940, 428.75],
          stops: [
            ["#FFB700", 0],
            ["rgba(255,183,0,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#FFC4DF", 3.0848],
          ["#FEF5F0", 80.72],
        ],
      },
      page: {
        opacity: 0.1,
        viewBox: [1781, 1304],
        layers: [
          {
            kind: "linear",
            angle: 336.64475,
            stops: [
              ["rgba(227,17,113,0)", 23.111],
              ["rgba(227,17,113,0.5)", 36.318],
              ["rgba(255,198,27,0.6)", 42.909],
              ["rgba(255,198,27,0)", 53.897],
            ],
          },
          {
            kind: "radial",
            opacity: 1,
            matrix: [-89.05, -65.2, 89.05, -65.2, 890.5, 652],
            stops: [
              ["#FFC61B", 0],
              ["rgba(255,198,27,0)", 0.6],
            ],
          },
        ],
      },
    },
    dark: {
      header: [
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [-28.8, 0, 0, -35, 1550, 350],
          stops: [
            ["#E31171", 0],
            ["rgba(227,17,113,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.9,
          matrix: [48.6, 0, 0, 19.6, 58, 61.25],
          stops: [
            ["#0A0B0F", 0],
            ["rgba(10,11,15,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.25,
          matrix: [32.111, 7.525, -2.8255, 36.925, 466, 330.75],
          stops: [
            ["#E31171", 0],
            ["rgba(227,17,113,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.4,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["rgba(255,183,0,0.84615)", 0.46635],
            ["rgba(255,183,0,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.4,
          matrix: [84.1, 1.75, -0.20833, 30.661, 940, 428.75],
          stops: [
            ["#FFB700", 0],
            ["rgba(255,183,0,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#8C0040", 3.0848],
          ["#0A0B0F", 80.72],
        ],
      },
      page: {
        opacity: 0.4,
        viewBox: [1781, 1297],
        layers: [
          {
            kind: "linear",
            angle: 158.05251,
            stops: [
              ["rgba(227,17,113,0)", 32.789],
              ["rgba(227,17,113,0.32)", 45.248],
              ["rgba(255,198,27,0.4)", 57.925],
              ["rgba(255,198,27,0)", 67.694],
            ],
          },
          {
            kind: "radial",
            opacity: 1,
            matrix: [89.05, 64.85, -89.05, 64.85, 890.5, 648.5],
            stops: [
              ["#E31171", 0],
              ["rgba(227,17,113,0)", 0.6],
            ],
          },
        ],
      },
    },
    noBg: { light: "#FEF5F0", dark: ["#0A0B0F", "#310B1F"] },
  },
  "palette:candy": {
    light: {
      header: [
        {
          kind: "radial",
          opacity: 0.2,
          matrix: [-28.8, 0, 0, -27.475, 1550, 350],
          stops: [
            ["#FFFFFF", 0],
            ["rgba(255,255,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.9,
          matrix: [48.6, 0, 0, 33.572, 58, 61.25],
          stops: [
            ["#FFFFFF", 0],
            ["rgba(255,255,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.2,
          matrix: [32.111, 7.525, -2.8255, 36.925, 466, 330.75],
          stops: [
            ["#C600D8", 0],
            ["rgba(198,0,216,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["rgba(255,183,0,0.84615)", 0.46635],
            ["rgba(255,183,0,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [84.1, 1.75, -0.20833, 30.661, 940, 428.75],
          stops: [
            ["#FFB700", 0],
            ["rgba(255,183,0,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#E5CFFF", 3.0848],
          ["#FEF5F0", 80.72],
        ],
      },
      page: {
        opacity: 0.1,
        viewBox: [1781, 1304],
        layers: [
          {
            kind: "linear",
            angle: 336.64475,
            stops: [
              ["rgba(239,69,255,0)", 23.111],
              ["rgba(239,69,255,0.5)", 36.318],
              ["rgba(255,183,0,0.6)", 42.909],
              ["rgba(255,183,0,0)", 53.897],
            ],
          },
          {
            kind: "radial",
            opacity: 1,
            matrix: [-89.05, -65.2, 89.05, -65.2, 890.5, 652],
            stops: [
              ["#5D00CF", 0],
              ["rgba(92,0,206,0)", 0.6],
            ],
          },
        ],
      },
    },
    dark: {
      header: [
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [-28.8, 0, 0, -35, 1550, 350],
          stops: [
            ["#C600D8", 0],
            ["rgba(198,0,216,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.9,
          matrix: [48.6, 0, 0, 13.775, 58, 61.25],
          stops: [
            ["#0A0B0F", 0],
            ["rgba(10,11,15,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.25,
          matrix: [32.111, 7.525, -2.8255, 36.925, 466, 330.75],
          stops: [
            ["#C600D8", 0],
            ["rgba(198,0,216,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["rgba(255,183,0,0.84615)", 0.46635],
            ["rgba(255,183,0,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [84.1, 1.75, -0.20833, 30.661, 940, 428.75],
          stops: [
            ["#FFB700", 0],
            ["rgba(255,183,0,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#270056", 0.85714],
          ["#0A0B0F", 80.72],
        ],
      },
      page: {
        opacity: 0.4,
        viewBox: [1781, 1301],
        layers: [
          {
            kind: "linear",
            angle: 157.99128,
            stops: [
              ["rgba(239,69,255,0)", 32.789],
              ["rgba(239,69,255,0.32)", 45.248],
              ["rgba(255,183,0,0.4)", 57.925],
              ["rgba(255,183,0,0)", 67.694],
            ],
          },
          {
            kind: "radial",
            opacity: 1,
            matrix: [89.05, 65.05, -89.05, 65.05, 890.5, 650.5],
            stops: [
              ["#5D00CF", 0],
              ["rgba(92,0,206,0)", 0.6],
            ],
          },
        ],
      },
    },
    noBg: { light: "#FEF5F0", dark: ["#0A0B0F", "#210933"] },
  },
  "palette:reef": {
    light: {
      header: [
        {
          kind: "linear",
          angle: 9.34317,
          stops: [
            ["rgba(255,255,255,0)", 34.057],
            ["rgba(255,255,255,0.6)", 55.058],
            ["rgba(255,255,255,0)", 75.659],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [-28.8, 0, 0, -27.475, 1550, 350],
          stops: [
            ["#29AFAA", 0],
            ["rgba(41,175,170,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.9,
          matrix: [48.6, 0, 0, 33.572, 58, 61.25],
          stops: [
            ["#FFFFFF", 0],
            ["rgba(255,255,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.2,
          matrix: [32.111, 7.525, -2.8255, 36.925, 466, 330.75],
          stops: [
            ["#29AFAA", 0],
            ["rgba(41,175,170,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["#F5961A", 0.46635],
            ["rgba(245,150,26,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [84.1, 1.75, -0.20833, 30.661, 940, 428.75],
          stops: [
            ["#29AFAA", 0],
            ["rgba(41,175,170,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#FFE4C0", 3.0848],
          ["#F9F2E9", 80.72],
        ],
      },
      page: {
        opacity: 0.1,
        viewBox: [1781, 1304],
        layers: [
          {
            kind: "linear",
            angle: 336.64475,
            stops: [
              ["rgba(41,175,170,0)", 23.111],
              ["#29AFAA", 36.318],
              ["#F5961A", 42.909],
              ["rgba(245,150,26,0)", 53.897],
            ],
          },
          {
            kind: "radial",
            opacity: 1,
            matrix: [-89.05, -65.2, 89.05, -65.2, 890.5, 652],
            stops: [
              ["#F5961A", 0],
              ["rgba(245,150,26,0)", 0.6],
            ],
          },
        ],
      },
    },
    dark: {
      header: [
        {
          kind: "linear",
          angle: 11.30994,
          stops: [
            ["rgba(255,191,105,0)", 32.3],
            ["rgba(255,191,105,0.3)", 50.263],
            ["rgba(255,191,105,0)", 67.549],
          ],
        },
        {
          kind: "radial",
          opacity: 0.2,
          matrix: [-28.8, 0, 0, -35, 1550, 350],
          stops: [
            ["#FFBF69", 0],
            ["rgba(255,191,105,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.6,
          matrix: [48.6, 0, 0, 19.6, 58, 61.25],
          stops: [
            ["#0A0B0F", 0],
            ["rgba(10,11,15,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [32.111, 7.525, -6.7008, 28.594, 466, 330.75],
          stops: [
            ["#30C4B6", 0],
            ["rgba(48,196,182,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.4,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["#FFA01C", 0.23148],
            ["rgba(255,160,28,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [84.1, 1.75, -0.20833, 30.661, 940, 428.75],
          stops: [
            ["#30C4B6", 0],
            ["rgba(48,196,182,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#4A1B0A", 3.0848],
          ["#0A0B0F", 80.72],
        ],
      },
      page: {
        opacity: 0.3,
        viewBox: [1781, 1244],
        layers: [
          {
            kind: "linear",
            angle: 158.86878,
            stops: [
              ["rgba(48,196,182,0)", 32.789],
              ["rgba(48,196,182,0.5)", 45.248],
              ["rgba(255,160,28,0.4)", 57.925],
              ["rgba(255,160,28,0)", 67.694],
            ],
          },
          {
            kind: "radial",
            opacity: 1,
            matrix: [89.05, 62.2, -89.05, 62.2, 890.5, 622],
            stops: [
              ["#FFBF69", 0],
              ["rgba(255,191,105,0)", 0.6],
            ],
          },
        ],
      },
    },
    noBg: { light: "#F9F2E9", dark: ["#0A0B0F", "#331308"] },
  },
  "palette:splash": {
    light: {
      header: [
        {
          kind: "linear",
          angle: 9.34317,
          stops: [
            ["rgba(255,255,255,0)", 34.057],
            ["rgba(255,255,255,0.6)", 55.058],
            ["rgba(255,255,255,0)", 75.659],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [-28.8, 0, 0, -27.475, 1550, 350],
          stops: [
            ["#6D7CFF", 0],
            ["rgba(109,124,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.9,
          matrix: [48.6, 0, 0, 33.572, 58, 61.25],
          stops: [
            ["#FFFFFF", 0],
            ["rgba(255,255,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [32.111, 7.525, -2.8255, 36.925, 466, 330.75],
          stops: [
            ["#00DAC2", 0],
            ["rgba(0,218,194,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["#0091FF", 0.46635],
            ["rgba(0,145,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.2,
          matrix: [84.1, 1.75, -0.20833, 30.661, 940, 428.75],
          stops: [
            ["#00DAC2", 0],
            ["rgba(0,218,194,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#DAEFFF", 3.0848],
          ["#EDF7FF", 80.72],
        ],
      },
      page: {
        opacity: 0.1,
        viewBox: [1781, 1296],
        layers: [
          {
            kind: "linear",
            angle: 336.77281,
            stops: [
              ["rgba(0,218,194,0)", 23.111],
              ["#00DAC2", 36.318],
              ["#0091FF", 42.909],
              ["rgba(0,145,255,0)", 53.897],
            ],
          },
          {
            kind: "radial",
            opacity: 1,
            matrix: [-89.05, -64.8, 89.05, -64.8, 890.5, 648],
            stops: [
              ["#0091FF", 0],
              ["rgba(0,145,255,0)", 0.6],
            ],
          },
        ],
      },
    },
    dark: {
      header: [
        {
          kind: "linear",
          angle: 9.34317,
          stops: [
            ["rgba(0,218,194,0)", 34.057],
            ["rgba(0,218,194,0.2)", 55.058],
            ["rgba(0,218,194,0)", 75.659],
          ],
        },
        {
          kind: "radial",
          opacity: 0.2,
          matrix: [-28.8, 0, 0, -27.475, 1550, 350],
          stops: [
            ["#6D7CFF", 0],
            ["rgba(109,124,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.9,
          matrix: [48.6, 0, 0, 33.572, 58, 61.25],
          stops: [
            ["#0A0B0F", 0],
            ["rgba(10,11,15,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.25,
          matrix: [32.111, 7.525, -2.8255, 36.925, 466, 330.75],
          stops: [
            ["#00DAC2", 0],
            ["rgba(0,218,194,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.4,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["#0091FF", 0.46635],
            ["rgba(0,145,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.2,
          matrix: [79.4, 2.225, -0.40546, 28.951, 940, 428.75],
          stops: [
            ["#00DAC2", 0],
            ["rgba(0,218,194,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#00223C", 3.0848],
          ["#0A0B0F", 80.72],
        ],
      },
      page: {
        opacity: 0.1,
        viewBox: [1781, 1296],
        layers: [
          {
            kind: "linear",
            angle: 336.77281,
            stops: [
              ["rgba(15,116,46,0)", 23.111],
              ["#0F742E", 36.318],
              ["#00B01E", 42.909],
              ["rgba(0,176,30,0)", 53.897],
            ],
          },
          {
            kind: "radial",
            opacity: 1,
            matrix: [-89.05, -64.8, 89.05, -64.8, 890.5, 648],
            stops: [
              ["#00DD70", 0],
              ["rgba(0,221,112,0)", 0.6],
            ],
          },
        ],
      },
    },
    noBg: { light: "#EDF7FF", dark: ["#0A0B0F", "#00213A"] },
  },
  "palette:dusk": {
    light: {
      header: [
        {
          kind: "linear",
          angle: 9.34317,
          stops: [
            ["rgba(255,255,255,0)", 34.057],
            ["rgba(255,255,255,0.6)", 55.058],
            ["rgba(255,255,255,0)", 75.659],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [-28.8, 0, 0, -27.475, 1550, 350],
          stops: [
            ["#FFC677", 0],
            ["rgba(255,198,119,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.9,
          matrix: [48.6, 0, 0, 33.572, 58, 61.25],
          stops: [
            ["#FFFFFF", 0],
            ["rgba(255,255,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [32.111, 7.525, -2.8255, 36.925, 466, 330.75],
          stops: [
            ["#FE6438", 0],
            ["rgba(254,100,56,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.4,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["#8C76CA", 0.46635],
            ["rgba(140,118,202,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [84.1, 1.75, -0.20833, 30.661, 940, 428.75],
          stops: [
            ["#FFC677", 0],
            ["rgba(255,198,119,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#E4DCFB", 3.0848],
          ["#F1EFF8", 80.72],
        ],
      },
      page: {
        opacity: 0.1,
        viewBox: [1781, 1296],
        layers: [
          {
            kind: "linear",
            angle: 336.64475,
            stops: [
              ["rgba(247,106,202,0)", 23.111],
              ["#F76ACA", 36.318],
              ["rgba(164,87,215,0.8)", 42.909],
              ["rgba(164,87,215,0)", 53.897],
            ],
          },
        ],
      },
    },
    dark: {
      header: [
        {
          kind: "linear",
          angle: 12.81664,
          stops: [
            ["rgba(255,198,119,0)", 38.082],
            ["rgba(255,198,119,0.15)", 54.99],
            ["rgba(255,198,119,0)", 70.286],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [-28.8, 0, 0, -35, 1550, 350],
          stops: [
            ["#B9A8EA", 0],
            ["rgba(185,168,234,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.7,
          matrix: [48.6, 0, 0, 19.6, 58, 61.25],
          stops: [
            ["#0A0B0F", 0],
            ["rgba(10,11,15,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [32.111, 7.525, -2.8255, 36.925, 466, 330.75],
          stops: [
            ["#FE6438", 0],
            ["rgba(254,100,56,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.5,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["#8C76CA", 0.46635],
            ["rgba(140,118,202,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.4,
          matrix: [84.1, 1.75, -0.54148, 26.022, 940, 428.75],
          stops: [
            ["#FFC677", 0],
            ["rgba(255,198,119,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#483778", 3.0848],
          ["#0A0B0F", 80.72],
        ],
      },
      page: {
        opacity: 0.3,
        viewBox: [1781, 1237],
        layers: [
          {
            kind: "linear",
            angle: 337.72477,
            stops: [
              ["rgba(38,113,255,0)", 23.111],
              ["#2671FF", 36.318],
              ["#A44EDD", 42.909],
              ["rgba(164,78,221,0)", 53.897],
            ],
          },
          {
            kind: "radial",
            opacity: 0.6,
            matrix: [-89.05, -61.85, 89.05, -61.85, 890.5, 618.5],
            stops: [
              ["#F870F4", 0],
              ["rgba(248,112,244,0)", 0.6],
            ],
          },
        ],
      },
    },
    noBg: { light: "#F1EFF8", dark: ["#0A0B0F", "#211E2C"] },
  },
  "palette:sunrise": {
    light: {
      header: [
        {
          kind: "linear",
          angle: 9.34317,
          stops: [
            ["rgba(255,255,255,0)", 34.057],
            ["rgba(255,255,255,0.6)", 55.058],
            ["rgba(255,255,255,0)", 75.659],
          ],
        },
        {
          kind: "radial",
          opacity: 0.9,
          matrix: [48.6, 0, 0, 33.572, 58, 61.25],
          stops: [
            ["#FFFFFF", 0],
            ["rgba(255,255,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [32.111, 7.525, -2.8255, 36.925, 466, 330.75],
          stops: [
            ["#FF7D1D", 0],
            ["rgba(255,125,29,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.4,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["#32AAFF", 0.46635],
            ["rgba(50,170,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.2,
          matrix: [84.1, 1.75, -0.20833, 30.661, 940, 428.75],
          stops: [
            ["#FFCD04", 0],
            ["rgba(255,205,4,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#BDE4FF", 3.0848],
          ["#EDF4FF", 80.72],
        ],
      },
      page: {
        opacity: 0.1,
        viewBox: [1781, 1304],
        layers: [
          {
            kind: "linear",
            angle: 336.64475,
            stops: [
              ["rgba(50,170,255,0)", 23.111],
              ["#32AAFF", 36.318],
              ["#FFCD04", 42.909],
              ["rgba(255,205,4,0)", 53.897],
            ],
          },
          {
            kind: "radial",
            opacity: 1,
            matrix: [-89.05, -65.2, 89.05, -65.2, 890.5, 652],
            stops: [
              ["#FFCD04", 0],
              ["rgba(255,205,4,0)", 0.6],
            ],
          },
        ],
      },
    },
    dark: {
      header: [
        {
          kind: "linear",
          angle: 9.34317,
          stops: [
            ["rgba(255,196,133,0)", 34.057],
            ["rgba(255,196,133,0.2)", 55.058],
            ["rgba(255,196,133,0)", 75.659],
          ],
        },
        {
          kind: "radial",
          opacity: 0.9,
          matrix: [48.6, 0, 0, 33.572, 58, 61.25],
          stops: [
            ["#0A0B0F", 0],
            ["rgba(10,11,15,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.3,
          matrix: [32.111, 7.525, -2.8255, 36.925, 466, 330.75],
          stops: [
            ["#FF7D1D", 0],
            ["rgba(255,125,29,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.4,
          matrix: [30.5, 39.55, -22.6, 53.375, 37, 47.25],
          stops: [
            ["#32AAFF", 0.46635],
            ["rgba(50,170,255,0)", 1],
          ],
        },
        {
          kind: "radial",
          opacity: 0.2,
          matrix: [84.1, 1.75, -0.20833, 30.661, 940, 428.75],
          stops: [
            ["#FFCD04", 0],
            ["rgba(255,205,4,0)", 1],
          ],
        },
      ],
      headerBase: {
        kind: "linear",
        angle: 180,
        stops: [
          ["#05316D", 3.0848],
          ["#0A0B0F", 80.72],
        ],
      },
      page: {
        opacity: 0.1,
        viewBox: [1781, 1304],
        layers: [
          {
            kind: "linear",
            angle: 336.64475,
            stops: [
              ["rgba(50,170,255,0)", 23.111],
              ["#32AAFF", 36.318],
              ["#FFCD04", 42.909],
              ["rgba(255,205,4,0)", 53.897],
            ],
          },
          {
            kind: "radial",
            opacity: 1,
            matrix: [-89.05, -65.2, 89.05, -65.2, 890.5, 652],
            stops: [
              ["#FFCD04", 0],
              ["rgba(255,205,4,0)", 0.6],
            ],
          },
        ],
      },
    },
    noBg: { light: "#EDF4FF", dark: ["#0A0B0F", "#0A1938"] },
  },
};
